import { useEffect, useMemo, useState } from 'react';
import { Box, Flex, Input, Spinner, Text, VStack } from '@chakra-ui/react';
import { Button as ButtonComponent, StyledCheckbox } from '@themes/aegix';
import { DrawerComponent } from '../ui/drawerComponent';
import { toaster } from '../ui/toaster';
import { useOrganizations } from '../../hooks/queries/useOrganizations';
import { useSetSchoolPreselectedProviderOrgs } from '../../hooks/queries/usePerSite';

interface PerSiteDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  schoolId?: string | number;
  action?: 'add' | 'edit';
  initialProviderOrgIds?: (string | number)[];
  onSaveTemp?: (selectedIds: string[]) => void;
}

export function PerSiteDrawer({
  isOpen,
  onClose,
  schoolId,
  action,
  initialProviderOrgIds,
  onSaveTemp,
}: PerSiteDrawerProps) {
  const activeSchoolId = schoolId ? String(schoolId) : '';

  const [selectedProviderIds, setSelectedProviderIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch provider org options
  const { data: orgsData, isLoading: isLoadingOrgs } = useOrganizations();

  const setPreselectedMutation = useSetSchoolPreselectedProviderOrgs();

  // Populate / reset state when drawer opens or initialProviderOrgIds change
  useEffect(() => {
    if (isOpen) {
      if (initialProviderOrgIds) {
        setSelectedProviderIds(initialProviderOrgIds.map((id) => String(id)));
      }
    } else {
      setSelectedProviderIds([]);
      setSearchQuery('');
    }
  }, [isOpen, initialProviderOrgIds]);

  const providerOrgs = useMemo(() => {
    return (orgsData ?? []).filter(
      (org) => org.is_provider && org.can_be_invited_as_provider,
    );
  }, [orgsData]);

  const filteredOrgs = useMemo(() => {
    if (!searchQuery.trim()) return providerOrgs;
    const q = searchQuery.toLowerCase();
    return providerOrgs.filter((org) => org.label.toLowerCase().includes(q));
  }, [providerOrgs, searchQuery]);

  const toggleProviderOrg = (orgIdStr: string) => {
    setSelectedProviderIds((prev) =>
      prev.includes(orgIdStr)
        ? prev.filter((id) => id !== orgIdStr)
        : [...prev, orgIdStr],
    );
  };

  const handleSave = async () => {
    const isAddMode = action === 'add' || !activeSchoolId;

    if (isAddMode) {
      if (onSaveTemp) {
        onSaveTemp(selectedProviderIds);
      }
      onClose();
      return;
    }

    try {
      await setPreselectedMutation.mutateAsync({
        school_id: activeSchoolId,
        provider_org_ids: selectedProviderIds,
      });
      toaster.create({
        title: 'Per-site provider organizations updated successfully',
        type: 'success',
      });
      if (onSaveTemp) {
        onSaveTemp(selectedProviderIds);
      }
      onClose();
    } catch (err: unknown) {
      const errObj = err as {
        detail?: string;
        message?: string;
      };
      const rawDetail = errObj?.detail || errObj?.message;
      const detail =
        typeof rawDetail === 'string' && rawDetail.trim()
          ? rawDetail.replace(/^provider_org_ids:\s*/i, '')
          : 'Failed to update per-site provider organizations';

      toaster.create({
        title: detail,
        type: 'error',
      });
    }
  };

  return (
    <DrawerComponent
      isOpen={isOpen}
      onClose={onClose}
      title="Per Site Configuration"
      size="sm"
      e2eTitle="perSiteDrawer"
      footer={
        <Flex gap={4} width="100%">
          <ButtonComponent
            visual="secondary"
            type="button"
            onClick={onClose}
            data-e2e="cancel-per-site-button"
            flex={1}
          >
            Cancel
          </ButtonComponent>
          <ButtonComponent
            visual="primary"
            type="button"
            onClick={() => void handleSave()}
            disabled={setPreselectedMutation.isPending}
            data-e2e="save-per-site-button"
            flex={1}
          >
            Save
          </ButtonComponent>
        </Flex>
      }
    >
      <VStack gap={4} align="stretch">
        <Box>
          <Text fontSize="sm" fontWeight={600} color="text.primary" mb={2}>
            Pre-selected Provider Organizations
          </Text>

          {isLoadingOrgs ? (
            <Flex align="center" gap={2} py={4}>
              <Spinner size="sm" color="button.primary" />
              <Text fontSize="sm" color="text.secondary">
                Loading provider configurations...
              </Text>
            </Flex>
          ) : (
            <VStack gap={3} align="stretch">
              {providerOrgs.length > 5 && (
                <Input
                  placeholder="Search provider organizations..."
                  size="sm"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  bg="surface.secondary"
                  data-e2e="search-provider-orgs-input"
                />
              )}

              <Box
                maxH="80"
                overflowY="auto"
                border="1px solid"
                borderColor="border.secondary"
                borderRadius="md"
                p={2}
              >
                {filteredOrgs.length === 0 ? (
                  <Text
                    fontSize="sm"
                    color="text.secondary"
                    py={2}
                    textAlign="center"
                  >
                    No provider organizations available
                  </Text>
                ) : (
                  filteredOrgs.map((org) => {
                    const orgIdStr = String(org.id);
                    const isChecked = selectedProviderIds.includes(orgIdStr);
                    return (
                      <Flex
                        key={org.id}
                        align="center"
                        gap={3}
                        p={2}
                        borderRadius="sm"
                        cursor="pointer"
                        _hover={{ bg: 'surface.secondary' }}
                        onClick={() => toggleProviderOrg(orgIdStr)}
                        data-e2e={`provider-org-item-${org.id}`}
                      >
                        <StyledCheckbox checked={isChecked} />
                        <Text fontSize="sm" color="text.primary" flex={1}>
                          {org.label}
                        </Text>
                      </Flex>
                    );
                  })
                )}
              </Box>
            </VStack>
          )}
        </Box>
      </VStack>
    </DrawerComponent>
  );
}
